<template>
  <Modal
    :isOpened="isOpened"
    :maxWidth="maxWidth"
    is-scrollable
    closeBoxTheme="dark"
    @close="closeModal"
  >
    <template v-slot>
      <v-card class="UploadFilesModal d-flex flex-column">
        <v-card-title class="UploadFilesModal_header ma-0 pa-8 d-block">
          <h1 class="UploadFilesModal_title">
            {{ title }}
          </h1>
          <h2 class="UploadFilesModal_subtitle">
            {{ $t('main.documents.wording.subtitle') }}
          </h2>
        </v-card-title>
        <v-card-text class="UploadFilesModal_text pa-8">
          <UploadFiles
            :files="files"
            :documentKind="documentKind"
            :documentStatus="documentStatus"
            :isInvalid="showIsInvalid"
            :isOpened="isOpened"
            @update:documentKind="$emit('update:documentKind', $event)"
            @appendFiles="onAppendFiles"
            @removeFile="onRemoveFile"
          />
        </v-card-text>
        <v-card-actions class="UploadFilesModal_footer ma-0 pa-8 d-flex justify-end">
          <v-btn
            text
            class="theme-btn app-button-secondary color-white nice-padding mr-8"
            @click="closeModal"
          >{{ $t('common.action.cancel') }}</v-btn>
          <v-btn
            :disabled="!saveButtonIsEnabled"
            :loading="btnLoader"
            text
            class="theme-btn app-button-primary color nice-padding"
            @click="uploadFile"
          >{{ $t('common.action.save') }}</v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </Modal>
</template>

<script>
  import UploadFiles from '@/components/ui/common/UploadFiles/UploadFiles'
  import Modal from '@/components/ui/common/Modal/Modal'
  import {
    uploadFiles,
    deleteFiles,
    deleteDocument,
  } from '@/components/ui/common/UploadFilesModal/UploadFilesModal.utils'
  import CommonMixin from '@/mixins/store/CommonMixin'

  export default {
    components: {
      UploadFiles,
      Modal,
    },
    mixins: [CommonMixin,],
    props: {
      isOpened: {
        type: Boolean,
        required: true,
      },
      maxWidth: {
        type: [String, Number,],
        default: 1200,
      },
      documentId: {
        type: String,
        default: '',
      },
      personId: {
        type: String,
        required: true,
      },
      documentKind: {
        type: String,
        required: true,
      },
      documentStatus: {
        type: String,
        required: true,
      },
      isInvalid: {
        type: Boolean,
        default: false,
      },
      documentFiles: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        newFiles: [],
        deletedFilesIds: [],
        btnLoader: false,
      }
    },
    computed: {
      title() {
        return this.documentKind
          ? this.$t(`main.enums.documents.${this.documentKind}`)
          : this.$tc('main.enums.sections.expected_supporting_documents', 0)
      },
      files() {
        const documentFiles = this.documentFiles.filter((documentFile) => {
          return this.deletedFilesIds.indexOf(documentFile.id) < 0
        })

        return [...documentFiles, ...this.newFiles,]
      },
      showIsInvalid() {
        if (this.isUpdated) {
          return false
        }

        return this.isInvalid
      },
      isUpdated() {
        return this.newFiles.length > 0 || this.deletedFilesIds.length > 0
      },
      saveButtonIsEnabled() {
        if (!this.documentKind) {
          return false
        }

        return this.isUpdated || this.status === 'sent_to_unicia'
      },
    },
    methods: {
      closeModal() {
        this.newFiles.splice(0, this.newFiles.length)
        this.deletedFilesIds.splice(0, this.deletedFilesIds.length)
        this.$emit('close')
      },
      onAppendFiles(files) {
        this.newFiles.push(...files)
      },
      onRemoveFile(file) {
        const documentFile = this.documentFiles.find((fileItem) => fileItem.id === file.id)
        if (documentFile) {
          this.deletedFilesIds.push(documentFile.id)
        }
        const newIndex = this.newFiles.findIndex((fileItem) => fileItem.id === file.id)
        if (newIndex >= 0) {
          this.newFiles.splice(newIndex, 1)
        }
      },
      setMessage(isSuccess, errorMessage) {
        if (isSuccess) {
          this.$message({
            type: 'success',
            text: 'file_upload_success',
          })
        } else {
          this.$message({
            type: 'error',
            text: errorMessage || 'file_upload_error',
          })
        }
      },
      async uploadFile() {
        this.toggleAppLoader(true)
        this.btnLoader = true
        try {
          if (this.newFiles.length === 0 && this.deletedFilesIds.length === this.documentFiles.length) {
            // delete document it-self
            await deleteDocument(this.documentId)
          } else {
            await deleteFiles(this.documentId, this.deletedFilesIds)
            await uploadFiles(
              this.$t.bind(this),
              this.newFiles,
              this.documentId,
              this.personId,
              this.documentKind
            )
          }

          this.setMessage(true)
          this.closeModal()
          this.$emit('updated')
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error(e)
          }
          this.setMessage(false, e.message)
        } finally {
          this.btnLoader = false
          this.toggleAppLoader(false)
        }
      },
    },
  }
</script>

<style scoped lang="scss">
.UploadFilesModal {
  &_text {
    overflow-y: auto;
    flex: 1 1 2000px;
  }
  &_header {
    background-color: $color-fafbff;
    box-shadow: inset 0 -1px 0 0 rgba(121, 137, 183, 0.16);
  }
  &_footer {
    background-color: $color-fafbff;
    box-shadow: inset 0 1px 0 0 rgba(121, 137, 183, 0.16);
  }
  &_title {
    font-size: 24px;
    color: $color-001c4b;
  }
  &_subtitle {
    font-size: 16px;
    color: $color-9e9fa2;
  }
}
</style>
